import {
  Datagrid,
  DatagridBody,
  DatagridConfigurable,
  EditButton,
  ExportButton,
  FilterButton,
  List,
  ListBase,
  Pagination,
  SelectColumnsButton,
  ShowButton,
  TopToolbar,
  useListContext,
  useNotify,
  useResourceContext,
  useResourceDefinition,
  useStore,
  WithRecord,
  WrapperField,
} from "react-admin";
import { debug_log } from "../utils/debugLog";
import { getStoreKey } from "../utils/getStoreKey";
import { removeFixedFiltersFromUIFilters } from "../utils/getUniqueUIFilters";
import {
  qualifyField,
  qualifyFilter,
  qualifyUIFilterAndAddLabel,
  unqualifyField,
  unqualifyFilter,
} from "../utils/qualifyFilter";
import { JsonStyled } from "./jsonStyled";
import { Box, Card, CardContent } from "@mui/material";
import { Debug } from "./debug";
import omit from "lodash/omit";
import camelCase from "lodash/camelCase";
import get from "lodash/get";
import merge from "lodash/merge";
import pick from "lodash/pick";
import PropTypes from "prop-types";
import { Settings } from "../utils/settings";
import { Empty } from "./Empty";
import { getConfig } from "../config/packageSetup";
import {
  asArray,
  expandCasings,
  flattenObject,
  humanize,
} from "../utils/tools";
import MyRow from "./myRow";
import { useRef, useState } from "react";
import { Store } from "../utils/store";
import { listSummary } from "../../config/settings";
import { Column } from "./column";
import { Upload } from "@mui/icons-material";
import { WithPermissions } from "../acl/withPermissions";
import { NoAccess } from "../acl/noAccess";
import { GrafanaButton } from "./grafanaButton";
import { DebugDotJson } from "./debugDotJson";
import { withErrorBoundaryManyInstance } from "./errorBoundary";
import { ListParamsWatcher, NamespacedURLSearchParams } from "./namespacedList";
import { useLocation } from "react-router-dom";
import { LinkButton } from "./linkButton";
import { customizeUiFilters } from "../utils/customizeUiFilters";
import { ImportButton as ImportButtonRA } from "react-admin-import-csv";
import { getErrorMessage } from "../utils/errors";
import { ManualNotification } from "./manualNotification";
import { onlyExistentFields } from "../acl/existentFields";
import { getValidResource } from "../acl/tools";
import { useRenderCounter } from "./renderCounter";
import { useMyContext } from "./myContext";
import { MyCreateButton } from "./myCreateButton";

const packageConfig = getConfig();

const getFrequencies = (arr) => {
  const f = {};

  arr.forEach((val) => {
    f[val] = f[val] || 0;
    f[val]++;
  });

  return f;
};

const getDuplicated = (arr) => {
  const f = getFrequencies(arr);
  return Object.keys(f).filter((k) => f[k] > 1);
};

const RemoveMissingFilters = ({ uiFilters, resource }) => {
  if (!uiFilters) return null;

  const { filterValues, setFilters } = useListContext();

  const storedFilterKeysQ = Object.keys(flattenObject(filterValues)).map(
    (storedFilterKey) => qualifyField(storedFilterKey, resource)
  );
  const uiFiltersKeysQ = uiFilters.map((uiFilter) =>
    qualifyField(uiFilter.props.source, resource)
  );

  const missing = storedFilterKeysQ.filter(
    (storedFilterKeyQ) => !uiFiltersKeysQ.includes(storedFilterKeyQ)
  );

  const duplicatedQ = getDuplicated(storedFilterKeysQ);
  const duplicated = duplicatedQ.concat(
    duplicatedQ.map((k) => unqualifyField(k))
  );

  const incorrect = missing.concat(duplicated);

  if (incorrect.length) {
    debug_log(
      `RemoveMissingFilters: Removing buggy stored filters: ${incorrect.join(
        ", "
      )}.`
    );
    const sanitizedFilters = omit(filterValues, incorrect);
    setFilters(sanitizedFilters);
  }

  return null;
};

const camelizeKeys = (o) => {
  if (!o) {
    return o;
  }

  const camel = {};

  Object.keys(o).forEach((key) => {
    camel[key] = o[key];
    if (key.includes("_")) {
      camel[camelCase(key)] = o[key];
    }
  });

  return camel;
};

const BulkButton = ({ enableBulkCreate, resourceData }) => {
  const resource = resourceData.name;
  return enableBulkCreate ? (
    <WithPermissions
      resource={resource}
      action={"bulk-create"}
      label={"BulkButton"}
    >
      <LinkButton
        startIcon={<Upload />}
        color="primary"
        label="Bulk Create"
        to={`/${resource}/bulk-create`}
      />
    </WithPermissions>
  ) : null;
};

const ImportButton = ({ enableImport, resourceData }) => {
  const [importError, setImportError] = useState("");

  const importOptions = {
    parseConfig: {
      // For all options see: https://www.papaparse.com/docs#config
      dynamicTyping: true,
    },
    postCommitCallback: (results) => {
      results.sort(
        (res1, res2) => get(res1, "value.id", 0) - get(res2, "value.id", 0)
      );

      const fail = results.filter((result) => !result.success);
      const error_msgs = fail.map((result, idx) => ({
        id: get(result, "value.id") || `row ${idx + 1}`,
        message: getErrorMessage(result.err),
      }));

      if (error_msgs.length) {
        const errors = error_msgs
          .map((error_msg) => `#${error_msg.id}: ${error_msg.message}`)
          .join("\n");

        setImportError(errors);
      }
    },
  };

  return enableImport && resourceData.hasCreate ? (
    <Box
      className="ImportButton"
      sx={{
        borderRadius: "5px",
        boxShadow:
          "0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)",
      }}
    >
      <ImportButtonRA {...importOptions} />
      {importError ? (
        <ManualNotification
          message={importError}
          type="error"
          close={() => setImportError("")}
        />
      ) : null}
    </Box>
  ) : null;
};

const ListActions = function ({ customActions, ...props }) {
  const resourceData = useResourceDefinition();

  return (
    <TopToolbar>
      {customActions}
      {props.supportDatagridConfigurable && (
        <SelectColumnsButton preferenceKey={props.preferenceKey} />
      )}
      <MyCreateButton
        customResourceData={props.resourceData}
        state={{ record: props.filter }}
      />
      {resourceData.hasList && <ExportButton />}
      <ImportButton {...props} />
      <BulkButton {...props} />
      <Debug>
        <GrafanaButton expr={` |= \`GET /backend/v1/${resourceData.name}\` `} />
      </Debug>
    </TopToolbar>
  );
};

const complexLabels = (children) =>
  children.filter(
    (child) =>
      child &&
      child.props &&
      child.props.label &&
      typeof child.props.label !== "string"
  );

const MyShowButton = ({ resourceData, showOnClick, customRowButtons }) => {
  if (
    resourceData.hasShow &&
    !showOnClick &&
    !customRowButtons.includes("show")
  ) {
    return (
      <WrapperField
        key={"show_button"}
        label="Show"
        source="Show"
        sortable={false}
      >
        <ShowButton
          label=""
          sx={{
            padding: "8px",
            minWidth: "0",
            "& .MuiButton-startIcon": {
              margin: "0",
            },
          }}
        />
      </WrapperField>
    );
  }

  return null;
};

const getRowButtons = ({ resourceData, customRowButtons }) => {
  const buttons = [];

  if (resourceData.hasEdit && !customRowButtons.includes("edit")) {
    buttons.push(
      <WrapperField key={"edit_button"} label="Edit" source="Edit">
        <EditButton />
      </WrapperField>
    );
  }

  return buttons;
};

const MyDatagridBody = ({ rowProps, ...rest }) => (
  <DatagridBody {...rest} row={<MyRow {...rowProps} />} />
);

export const ListHeader = ({
  resource,
  actions,
  title,
  forceHeaders,
  ...props
}) => {
  const pageResource = useResourceContext();
  const isEmbeded = resource && pageResource && pageResource != resource;

  if (isEmbeded && !forceHeaders) return null;

  resource = resource || pageResource;

  return (
    <Box className="ListHeader" {...props}>
      <Box
        className="ListTitleWrapper"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Column sx={{ flexWrap: "wrap" }}>
          <Box className="ListTitle">{humanize(title || resource)}</Box>
          <Box className="ListSummary" sx={{ marginTop: "12px" }}>
            {listSummary[resource]}
          </Box>
        </Column>
        <Box
          className="ActionsWrapper"
          sx={{ "& .MuiToolbar-root": { minHeight: "auto" } }}
        >
          {actions}
        </Box>
      </Box>
    </Box>
  );
};

const getCoallescedParams = (listProps, queryParams) => {
  const defaults = { page: 1 };
  const _listProps = pick(listProps, [
    "perPage",
    "sort",
    "filterDefaultValues",
  ]);

  const _queryParams = pick(queryParams, [
    "page",
    "perPage",
    "displayedFilters",
  ]);

  if (queryParams.sort && queryParams.order) {
    _queryParams.sort = {
      field: queryParams.sort,
      order: queryParams.order,
    };
  }
  if (queryParams.filter) {
    _queryParams.filterDefaultValues = queryParams.filter;
  }

  return { ...defaults, ..._listProps, ..._queryParams };
};

const processUIFlters = (
  filters,
  filter,
  resource,
  filtersFilter,
  permissionAction,
  removedCustomFilters
) => {
  if (!filters) return filters;

  if (filtersFilter) {
    filters = filters.filter(filtersFilter);
  }

  filters = onlyExistentFields(
    filters,
    resource,
    permissionAction,
    null,
    removedCustomFilters,
    "filters"
  );

  filters = removeFixedFiltersFromUIFilters(filters, filter);
  filters = qualifyUIFilterAndAddLabel(filters, resource);
  filters = customizeUiFilters(filters);

  if (filters.length) {
    filters.unshift(
      <FilterButton
        source="FilterButtonToUseAsKey"
        filters={filters}
        alwaysOn
        disableSaveQuery
        className="FilterButton"
        sx={{
          button: {
            fontSize: 0 /* hide label */,
            padding: "14px 20px",
          },
          ".MuiButton-startIcon": {
            margin: 0,
          },
          svg: {
            fontSize: "1.8rem !important",
          },
        }}
      />
    );
  }

  return filters;
};

export const MyList = ({
  fieldFilter,
  filtersFilter,
  forcedCustomFields,
  removedCustomFields,
  queryOptions = {},
  customRowButtons = [],
  enableBulkCreate,
  enableImport,
  noHeaders,
  customHeader,
  forceHeaders,
  forceShow, //workaround for permisions on non defined resources TODO: make this better
  title,
  children,
  rowProps,
  dataGridProps: _dataGridProps = {},
  customActions = [],
  helperText = null,
  permissionAction = "list",
  removedCustomFilters,
  ...listProps
}) => {
  const notify = useNotify();
  const { acp } = useMyContext();

  const pageResource = getValidResource(useResourceContext());
  const resource = (listProps.resource = getValidResource(listProps.resource));
  useRenderCounter(`List ${resource}`);

  const isEmbeded = resource && pageResource && pageResource != resource;
  const showHeader = forceHeaders || !(noHeaders || isEmbeded);
  const showOnClick = Settings.getValue("showOnClick");
  const storeKey = getStoreKey({ resource });

  const [storedData] = useStore(storeKey);
  const resourceData = useResourceDefinition({ resource });

  children = asArray(children).filter((v) => v);
  if (fieldFilter) {
    children = children.filter(fieldFilter);
  }

  children = onlyExistentFields(
    children,
    resource,
    permissionAction,
    forcedCustomFields,
    removedCustomFields
  );

  const columnSources = children.map((ch) => get(ch, "props.source")); //for debug

  const queryParamManager = NamespacedURLSearchParams(
    resource,
    useLocation().search
  );
  const allQueryParams = queryParamManager.getAll();
  const queryParams = queryParamManager.get();

  // Patch: persistent selected ids
  // "Selection state will remain linked to a resource-based key regardless of the specified storeKey string. This is a design choice..."
  const renderCount = useRef(0);
  renderCount.current += 1;
  if (renderCount.current < 2) {
    Store.set(`RaStore.${resource}.selectedIds`, []);
  }

  //https://github.com/marmelab/react-admin/blob/master/CHANGELOG.md#v4104
  const supportDatagridConfigurable = !complexLabels(children).length;
  const Grid = supportDatagridConfigurable ? DatagridConfigurable : Datagrid;
  const preferenceKey = supportDatagridConfigurable ? storeKey : null;

  debug_log(`<<<<<<<<<<<< List for '${resource}' <<< start`);

  const coallescedParams = getCoallescedParams(listProps, queryParams);
  listProps = { ...listProps, ...coallescedParams };

  let { filters, filter, filterDefaultValues, ...restListProps } = listProps;

  filters = processUIFlters(
    filters,
    filter,
    resource,
    filtersFilter,
    permissionAction,
    removedCustomFilters
  );
  filterDefaultValues = qualifyFilter(filterDefaultValues, resource);
  filter = qualifyFilter(filter, resource);

  listProps = Object.assign(
    { perPage: packageConfig.listPageSize },
    restListProps,
    {
      filter,
      filters: Array.isArray(filters) && !filters.length ? null : filters,
      filterDefaultValues,
      storeKey,

      empty: (
        <Empty
          buttonProps={{ state: { record: camelizeKeys(listProps.filter) } }}
        />
      ),
      queryOptions: merge(
        {},
        {
          meta: {
            notify,
            page: listProps.page,
            isSuperuser: acp?.isSuperuser(),
          },
        },
        queryOptions
      ),
      disableSyncWithLocation: true,
      pagination: <Pagination page={listProps.page} />,
    }
  );

  let {
    omit = [],
    bulkActionButtons = false,
    ...dataGridProps
  } = _dataGridProps;

  omit = expandCasings(Object.keys(unqualifyFilter(filter))).concat(omit);

  dataGridProps = Object.assign(
    { body: <MyDatagridBody rowProps={rowProps} /> },
    { rowClick: resourceData.hasShow && showOnClick ? "show" : null },
    preferenceKey ? { preferenceKey } : {},
    dataGridProps,
    { omit, bulkActionButtons }
  );

  const actionsProps = {
    preferenceKey,
    resourceData,
    supportDatagridConfigurable,
    filter: camelizeKeys(unqualifyFilter(filter)),
    enableBulkCreate,
    enableImport,
    customActions,
  };

  const actions = <ListActions {...actionsProps} />;

  debug_log(`>>>>>>>>>>>> List for '${resource}' >>> end`);

  return resourceData.hasList || forceShow ? (
    <Column className="MyListWrapper">
      {customHeader}
      {showHeader && (
        <ListBase {...listProps}>
          <ListHeader
            resource={resource}
            actions={actions}
            title={title}
            forceHeaders={forceHeaders}
          />
        </ListBase>
      )}
      {helperText}
      <List
        className="MyList"
        {...listProps}
        actions={showHeader ? null : actions}
      >
        <Grid {...dataGridProps}>
          <MyShowButton {...{ resourceData, showOnClick, customRowButtons }} />
          {withErrorBoundaryManyInstance(children)}
          {getRowButtons({ resourceData, showOnClick, customRowButtons })}
          {Settings.debugging() && (
            <WithRecord
              source="__row"
              label="__row"
              render={(record) => <DebugDotJson data={record} />}
            />
          )}
        </Grid>
        <ListParamsWatcher namespace={resource} />
        <RemoveMissingFilters uiFilters={filters} resource={resource} />
      </List>
      <Debug foldable={true}>
        <Card>
          <CardContent>
            <h3>Fixed filter</h3>
            <JsonStyled data={filter} />
            <h3>List setup</h3>
            <JsonStyled
              data={{
                coallescedParams,
                allQueryParams,
                resource,
                resourceData,
                storeKey,
                storedData,
                preferenceKey,
                supportDatagridConfigurable,
                showOnClick,
                filterDefaultValues,
                columnSources,
              }}
            />
          </CardContent>
        </Card>
      </Debug>
    </Column>
  ) : (
    <NoAccess label={`List::${resource}`} />
  );
};

MyList.propTypes = {
  resource: PropTypes.string.isRequired,
};
